/**
 * age.ts — a single, side-effect-free age computation (s149 CHILD-OVERFLOW-1 Phase 2).
 *
 * `ageAtDeath` returns whole years between a date of birth and a reference date (the deceased's date of
 * death), or null when either input is missing or unparseable. DISPLAY-ONLY: it never gates, filters, or
 * excludes — the executor holds the binder and can change any fact (D-144.4). The picker uses it to label
 * a child with their age at the date of death; a missing/blank DOB simply shows no age (no error).
 */

/** Whole years from `dob` to `dateOfDeath` (both ISO `YYYY-MM-DD`), or null if either is missing/invalid. */
export function ageAtDeath(
  dob: string | null | undefined,
  dateOfDeath: string | null | undefined,
): number | null {
  if (!dob || !dateOfDeath) return null;
  const birth = new Date(dob);
  const death = new Date(dateOfDeath);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(death.getTime())) return null;

  let age = death.getFullYear() - birth.getFullYear();
  const monthDelta = death.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && death.getDate() < birth.getDate())) age -= 1;

  // Guard nonsense (death before birth, or a typo'd century) rather than surfacing a negative/absurd age.
  if (age < 0 || age > 130) return null;
  return age;
}
